import { Injectable, OnDestroy } from '@angular/core';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { environment } from '@env/environment';
import { logger } from '@shared/utils/functions.utils';
import { Subject, Observable, filter, map } from 'rxjs';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root',
})
export class SucursalListenerService implements OnDestroy {
  constructor(private socketService: SocketService) {}
  private readonly namespace = '/sucursal';

  // Subject privado (no exponer públicamente)
  private readonly eventNotificationSubject = new Subject<IEventNotification>();

  // Observable público readonly (inmutable desde fuera)
  readonly events$: Observable<IEventNotification> = this.eventNotificationSubject.asObservable();

  /**
   * Filtra eventos por tipo específico
   * @param eventType Tipo de evento a filtrar
   */
  onEvent<T = unknown>(eventType: EVENT_GATEWAY): Observable<IEventNotification<T>> {
    return this.events$.pipe(
      filter((notification) => notification.event === eventType),
    ) as Observable<IEventNotification<T>>;
  }

  /**
   * Obtiene solo los datos del evento, sin metadata
   * @param eventType Tipo de evento
   */
  getData<T = unknown>(eventType: EVENT_GATEWAY): Observable<T> {
    return this.onEvent<T>(eventType).pipe(map((notification) => notification.data));
  }

  /**
   * Filtra eventos por sucursal
   * @param sucursalId ID de la sucursal
   */
  onSucursal(sucursalId: number): Observable<IEventNotification> {
    return this.events$.pipe(filter((notification) => notification.sucursalId === sucursalId));
  }

  /**
   * Detiene el listener y limpia recursos
   */
  stop(sucursal_id: number): void {
    const socket = this.socketService.obtenerSocket(sucursal_id, this.namespace);
    if (!socket) return;

    // Remover listeners para evitar fugas de memoria
    socket.off(EVENT_GATEWAY.CONNECT);
    socket.off(EVENT_GATEWAY.DISCONNECT);
    socket.off(EVENT_GATEWAY.SUCURSAL_PRINT_UPDATE);

    this.socketService.desconectarSocketEspecifico(sucursal_id, this.namespace);
  }

  /**
   * Inicia el listener para una sucursal
   */
  start(sucursal_id: number): void {
    const socket = this.socketService.conectarSucursal(sucursal_id, this.namespace);
    // Log adicional de debug (solo en desarrollo)
    if (!environment.production) {
      logger('[SucursalListener] Estado:', {
        connected: socket?.connected,
        namespace: this.namespace,
        id: socket?.id,
      });
    }

    if (!socket) {
      console.error('[SucursalListener] No se pudo obtener la instancia del socket');
      return;
    }

    if (!environment.production) {
      logger('[SucursalListener] Inicializando listeners...');
    }

    // Evento de conexión exitosa
    socket.on(EVENT_GATEWAY.CONNECT, () => {
      if (!environment.production) {
        logger('[SucursalListener] ✅ Conectado al servidor WebSocket');
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.CONNECT,
        data: null,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Evento de desconexión
    socket.on(EVENT_GATEWAY.DISCONNECT, (reason: string) => {
      logger('[SucursalListener] ❌ Desconectado del servidor:', reason);
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.DISCONNECT,
        data: reason,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Listeners de eventos de comanda
    socket.on(EVENT_GATEWAY.SUCURSAL_PRINT_UPDATE, (data) => {
      if (!environment.production) {
        logger('[SucursalListener] 📋 Actualización de impresión para sucursal:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.SUCURSAL_PRINT_UPDATE,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Log del estado de conexión inicial (solo en desarrollo)
    if (!environment.production) {
      logger(
        `[SucursalListener] Estado inicial: ${socket.connected ? '✅ Conectado' : '⏳ Conectando...'}`,
      );
    }
  }

  /**
   * Limpieza automática cuando el servicio es destruido
   */
  ngOnDestroy(): void {
    this.eventNotificationSubject.complete();
  }
}
